import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { OptionalRender } from "../../components/Common/OptionalRender";
import { PageTitle } from "../../components/Common/PageTitle";
import { EditProjectDetailsModal } from "../../components/ProjectManagementComponents/EditProjectDetailsModal";
import { ProjectBasicInfo } from "../../components/ProjectManagementComponents/ProjectBasicInfo";
import { ProjectCustomNotes } from "../../components/ProjectManagementComponents/ProjectCustomNotes";
import { ProjectFacilitiesList } from "../../components/ProjectManagementComponents/ProjectFacilitiesList";
import { ProjectFacilityDetailsModal } from "../../components/ProjectManagementComponents/ProjectFacilitiesList/ProjectFacilityDetailsModal";
import { useMeWithOrganizationImpersonation } from "../../hooks/organizationImpersonation";
import { useValidateOrganization } from "../../hooks/validateUserOrganization";
import { AccountType } from "../../models/AccountData";
import DataSvc from "../../services/dataSvc";
import DemoModeSvc from "../../services/demoModeSvc";

import "./styles.scss";

type IProjectManagementPageProps = RouteComponentProps<any>;

const ProjectManagementPage: React.FunctionComponent<
  IProjectManagementPageProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`projectManagementPage.${key}`);

  useMeWithOrganizationImpersonation();
  const matchOrganizationId = props.match.params.organizationId;

  useValidateOrganization({
    matchOrganizationId,
    redirectUrlFormat: (orgId) => `/project/organization/${orgId}`
  });

  const [isEditProjectDetailsModalOpen, setIsEditProjectDetailsModalOpen] =
    useState(false);

  const [
    selectedProjectLocationDetailsId,
    setSelectedProjectLocationDetailsId
  ] = useState<string | undefined>(undefined);

  const { data: projects } = useQuery({
    queryKey: ["projects", matchOrganizationId],
    queryFn: () => DataSvc.getProjects({ organizationId: matchOrganizationId })
  });

  // This will be used in the future to allow selecting a project from the list.
  // For now, multiple projects is not supported, and we only show the first project.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedProjectIndex, setSelectedProjectIndex] = useState<number>(0);
  const selectedProject = projects?.[selectedProjectIndex];

  const handleOpenProjectLocationDetailsModal = (projectLocationId: string) => {
    setSelectedProjectLocationDetailsId(projectLocationId);
  };

  return (
    <div className="right-content project-management-page">
      <div className="top-title-bar">
        <PageTitle text={t("project_management_page")} />
        {/* The edit form shows the real project name, which often contains the organization name */}
        <OptionalRender
          mustBeOneOfRoles={[AccountType.ADMIN]}
          customCondition={() => !DemoModeSvc.current.isDemoModeOn}
        >
          <button
            className="btn btn-blue"
            onClick={() => setIsEditProjectDetailsModalOpen(true)}
          >
            {t("edit_project_details")}
          </button>
        </OptionalRender>
      </div>
      <div className="line"></div>
      <ProjectBasicInfo project={selectedProject} />
      <ProjectFacilitiesList
        project={selectedProject}
        onOpenProjectLocationDetailsModal={
          handleOpenProjectLocationDetailsModal
        }
      />
      <ProjectCustomNotes project={selectedProject} />
      {isEditProjectDetailsModalOpen && selectedProject && (
        <EditProjectDetailsModal
          handleClose={() => setIsEditProjectDetailsModalOpen(false)}
          project={selectedProject}
        />
      )}
      {selectedProjectLocationDetailsId && (
        <ProjectFacilityDetailsModal
          organizationId={selectedProject.organization_id}
          projectId={selectedProject.id.toString()}
          projectLocationId={selectedProjectLocationDetailsId}
          handleClose={() => setSelectedProjectLocationDetailsId(undefined)}
        />
      )}
    </div>
  );
};

export default withRouter(ProjectManagementPage);
